/**
 * packages/auth-client/src/useAuth.js
 * React hook for auth state in core-loop.dev browser apps.
 * Requires React 18+ as a peer dependency.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  decodeJwtPayload,
  isTokenExpired,
  getStoredToken,
  setStoredToken,
  clearStoredToken,
  getStoredRefreshToken,
  setStoredRefreshToken,
  clearStoredRefreshToken,
} from './utils.js';

interface UseAuthOptions {
  /**
   * Auth API base URL.
   * When provided, the hook will automatically refresh the access token
   * ~2 minutes before it expires using the stored refresh token cookie.
   */
  refreshUrl?: string;
}

export function useAuth(opts?: UseAuthOptions) {
  // Determine initial token synchronously to avoid a render-cycle flash.
  const initialToken = (() => {
    const stored = getStoredToken();
    if (stored && !isTokenExpired(stored)) return stored;
    clearStoredToken();
    return null;
  })();

  const [token, setToken] = useState<string | null>(initialToken);

  // If the access token is missing/expired but a refresh token exists and a
  // refreshUrl is provided, attempt a silent refresh before showing any UI.
  // isLoading stays true until that attempt completes (or there's nothing to try).
  const needsSilentRefresh = !initialToken && !!opts?.refreshUrl && !!getStoredRefreshToken();
  const [isLoading, setIsLoading] = useState(needsSilentRefresh);

  useEffect(() => {
    if (!needsSilentRefresh || !opts?.refreshUrl) return;
    let cancelled = false;
    (async () => {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) { setIsLoading(false); return; }
      try {
        const res = await fetch(`${opts.refreshUrl}/api/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json() as { accessToken: string; refreshToken?: string };
            setStoredToken(data.accessToken);
            if (data.refreshToken) setStoredRefreshToken(data.refreshToken);
            setToken(data.accessToken);
          } else {
            clearStoredToken();
            clearStoredRefreshToken();
          }
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, []);

  // Auto-refresh: schedule a refresh ~2 min before access token expiry
  useEffect(() => {
    if (!opts?.refreshUrl || !token) return;

    const payload = decodeJwtPayload(token);
    const exp = typeof payload?.exp === 'number' ? payload.exp : null;
    if (!exp) return;

    const expiresInMs = exp * 1000 - Date.now();
    const refreshInMs = Math.max(expiresInMs - 2 * 60 * 1000, 0);

    if (refreshInMs <= 0) return; // already expired

    const timer = setTimeout(async () => {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) return;
      try {
        const res = await fetch(`${opts.refreshUrl}/api/auth/token/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (res.ok) {
          const data = await res.json() as { accessToken: string; refreshToken?: string };
          setStoredToken(data.accessToken);
          if (data.refreshToken) setStoredRefreshToken(data.refreshToken);
          setToken(data.accessToken);
        } else {
          // Refresh rejected -- force re-login
          clearStoredToken();
          clearStoredRefreshToken();
          setToken(null);
        }
      } catch {
        // Network error -- don't clear; will retry on next token change
      }
    }, refreshInMs);

    return () => clearTimeout(timer);
  }, [token, opts?.refreshUrl]);

  const login = useCallback((newToken: string, newRefreshToken?: string) => {
    setStoredToken(newToken);
    if (newRefreshToken) setStoredRefreshToken(newRefreshToken);
    setToken(newToken);
  }, []);

  const logout = useCallback((redirectUrl?: string) => {
    clearStoredToken();
    clearStoredRefreshToken();
    setToken(null);
    if (typeof window !== 'undefined') {
      // Default to the current app's origin so each app stays on its own domain
      // after sign-out. Pass a string redirectUrl to override (e.g. to a specific path).
      // Guard: ignore non-string arguments (e.g. click events passed by onClick handlers).
      const dest = typeof redirectUrl === 'string' ? redirectUrl : window.location.origin;
      window.location.href = dest;
    }
  }, []);

  const user = token && !isTokenExpired(token) ? decodeJwtPayload(token) : null;

  return { user, token, isLoading, login, logout };
}
